import { useState, useEffect } from 'react';
import { newsApiService } from '../services/newsApi';
import type { NewsApiResponse, NewsApiArticle } from '../services/newsApi';

export interface UseNewsApiResult {
  articles: NewsApiArticle[];
  loading: boolean;
  error: string | null;
  hasMore: boolean;
  nextPage: string | null;
  loadMore: () => Promise<void>;
  refresh: () => Promise<void>;
  totalResults: number;
}

export function useNewsApi(category: string = 'Todas', lang?: string): UseNewsApiResult {
  const [articles, setArticles] = useState<NewsApiArticle[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [nextPage, setNextPage] = useState<string | null>(null);
  const [totalResults, setTotalResults] = useState(0);

  const fetchNews = async (page?: string, append: boolean = false) => {
    try {
      setLoading(true);
      setError(null);
      
      const response: NewsApiResponse = await newsApiService.fetchLiteraryNews(category, page, lang);
      
      if (append) {
        setArticles(prev => [...prev, ...response.results]);
      } else {
        setArticles(response.results);
      }
      
      setNextPage(response.nextPage);
      setTotalResults(response.totalResults);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Error desconocido al cargar noticias');
      console.error('Error fetching news:', err);
    } finally {
      setLoading(false);
    }
  };

  const loadMore = async () => {
    if (nextPage && !loading) {
      await fetchNews(nextPage, true);
    }
  };

  const refresh = async () => {
    setArticles([]);
    setNextPage(null);
    await fetchNews();
  };

  useEffect(() => {
    fetchNews();
  }, [category, lang]);

  return {
    articles,
    loading,
    error,
    hasMore: !!nextPage,
    nextPage,
    loadMore,
    refresh,
    totalResults
  };
}
